import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'

import { CustomerFormInitialValues } from '@features/contacts/elements/CustomerModal'
import { CustomerType } from '@features/contacts/enums/customerType'
import {
  ImportedInvoiceExtractionAddress,
  ImportedInvoiceExtractionParty,
} from '@features/importedInvoices/types/importedInvoiceExtraction'
import { getNormalizedImportedInvoiceTaxNumber } from '@features/importedInvoices/utils/getNormalizedImportedInvoiceTaxNumber'

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/

const getTrimmed = (value?: string | null): string | undefined => value?.trim() || undefined

const getCountry = (country?: string | null): string | undefined => {
  const countryCode = country?.trim().toUpperCase()

  return countryCode && COUNTRY_CODE_PATTERN.test(countryCode) ? countryCode : undefined
}

// A missing value is left out rather than set, so it cannot clear a default the modal starts with.
const getAddress = (address?: ImportedInvoiceExtractionAddress | null): CustomerFormInitialValues['address'] =>
  omitBy(
    {
      city: getTrimmed(address?.city),
      country: getCountry(address?.country),
      pobox: getTrimmed(address?.postalCode),
      streetAddress1: getTrimmed(address?.line1),
    },
    isNil,
  )

const getPersonName = (name?: string): Pick<CustomerFormInitialValues, 'firstName' | 'lastName'> => {
  const [firstName, ...lastNames] = name?.split(/\s+/) ?? []

  return omitBy({ firstName, lastName: lastNames.join(' ') || undefined }, isNil)
}

/**
 * The customer form for creating the extracted client. A tax number makes it a company; without one
 * it starts as an individual, whose extracted name is split into first and last name.
 */
export const getImportedInvoiceCustomerFormValues = (
  client?: ImportedInvoiceExtractionParty | null,
): { customerType: CustomerType; initialValues: CustomerFormInitialValues } => {
  const name = getTrimmed(client?.name)
  const vatNumber = getNormalizedImportedInvoiceTaxNumber(client?.vatNo)
  const businessId = getNormalizedImportedInvoiceTaxNumber(client?.registrationNo)
  const address = getAddress(client?.address)

  if (vatNumber || businessId) {
    return {
      customerType: CustomerType.Company,
      initialValues: { address, ...omitBy({ businessId, companyName: name, vatNumber }, isNil) },
    }
  }

  return { customerType: CustomerType.Individual, initialValues: { address, ...getPersonName(name) } }
}
